import { useNavigate } from 'react-router-dom';
import Typography from '../../sharedComponents/Typography/Typography';
import { GeoSun } from '../../sharedComponents/assets/svg-tsx/sensible';
import { i18nformatDateRangeLong } from '../../utils/i18n';
import type { PolicyBundle } from '../../utils/types/Entity';
import DashboardIcon from './DashboardIcon';
import { getBundleLocation } from './policyBundleSummary';
import styles from './home-authed.module.scss';

type CoverageSectionProps = {
  policyBundlesReady: boolean;
  featuredBundle?: PolicyBundle;
  isCurrent: boolean;
};

const CoverageSection: React.FC<CoverageSectionProps> = ({
  policyBundlesReady,
  featuredBundle,
  isCurrent,
}) => {
  const navigate = useNavigate();

  return (
    <section className={styles.dashboardSection} aria-labelledby="coverage">
      <div className={styles.sectionHeading}>
        <div id="coverage">
          <Typography variant="label-1" className={styles.sectionEyebrow}>
            YOUR COVERAGE
          </Typography>
          <Typography variant="h4" className={styles.sectionTitle}>
            {featuredBundle ? 'On the horizon' : 'Plan something wonderful'}
          </Typography>
        </div>
        <button
          type="button"
          className={styles.textLink}
          onClick={() => navigate('/trips')}
        >
          All trips
          <DashboardIcon name="arrow" />
        </button>
      </div>

      {!policyBundlesReady ? (
        <div className={styles.loadingCard} aria-live="polite">
          <span className={styles.loadingLine} />
          <span className={styles.loadingLineShort} />
          <span className={styles.loadingBlock} />
          <span className={styles.srOnly}>Loading your coverage</span>
        </div>
      ) : featuredBundle ? (
        <button
          type="button"
          className={styles.coverageCard}
          onClick={() => navigate(`/trips/${featuredBundle.slug}`)}
          aria-label={`View Weather Guarantee for ${getBundleLocation(
            featuredBundle,
          )}`}
        >
          <div className={styles.coverageTopline}>
            <span className={styles.coverageBadge}>
              <span />
              {isCurrent ? 'HAPPENING NOW' : 'UP NEXT'}
            </span>
            <span className={styles.cardArrow}>
              <DashboardIcon name="arrow" />
            </span>
          </div>
          <Typography variant="h4" className={styles.location}>
            {getBundleLocation(featuredBundle)}
          </Typography>
          <Typography variant="body-2" className={styles.tripDates}>
            {i18nformatDateRangeLong(
              featuredBundle.coverageStartDate,
              featuredBundle.coverageEndDate,
            )}
          </Typography>
          <div className={styles.coverageFooter}>
            <span className={styles.weatherMark} aria-hidden="true">
              <span className={styles.weatherSun} />
              <span className={styles.weatherCloud} />
            </span>
            <div>
              <Typography variant="label-3">Weather Guarantee</Typography>
              <Typography variant="body-4" color="midnight-60">
                Weather monitoring is on
              </Typography>
            </div>
          </div>
        </button>
      ) : (
        <div className={styles.emptyCard}>
          <div className={styles.emptyIllustration} aria-hidden="true">
            <GeoSun />
            <DashboardIcon name="map" />
          </div>
          <div className={styles.emptyCopy}>
            <Typography variant="h5">Nothing on the calendar yet</Typography>
            <Typography variant="body-3" color="midnight-60">
              Protect your next trip from bad weather and enjoy the forecast
              with a little less worry.
            </Typography>
          </div>
          <button
            type="button"
            className={styles.primaryLink}
            onClick={() => navigate('/purchase-guarantee')}
          >
            Find Weather Guarantee coverage
            <DashboardIcon name="arrow" />
          </button>
        </div>
      )}
    </section>
  );
};

export default CoverageSection;
